import { useState } from 'react';
import { HeroSettings } from '@/hooks/useHeroSettings';

interface HeroEditorProps {
  settings: HeroSettings;
  onSave: (s: HeroSettings) => void;
}

const bgPositions = [
  { value: 'center', label: 'Centro' },
  { value: '80% center', label: 'Direita (80%)' },
  { value: '90% center', label: 'Direita (90%)' },
  { value: '95% center', label: 'Direita (95%)' },
  { value: 'right', label: 'Extrema direita' },
  { value: 'left', label: 'Esquerda' },
  { value: 'top', label: 'Topo' },
  { value: 'bottom', label: 'Base' },
  { value: 'right top', label: 'Direita + Topo' },
  { value: 'right bottom', label: 'Direita + Base' },
  { value: 'left top', label: 'Esquerda + Topo' },
  { value: 'left bottom', label: 'Esquerda + Base' },
];

const bgSizes = [
  { value: 'cover', label: 'Cover (cobrir tudo)' },
  { value: 'contain', label: 'Contain (caber todo)' },
  { value: '100% 100%', label: 'Esticar (100%)' },
  { value: '50% auto', label: '50% largura' },
  { value: '75% auto', label: '75% largura' },
  { value: '125% auto', label: '125% largura' },
  { value: '150% auto', label: '150% largura' },
  { value: '200% auto', label: '200% largura' },
  { value: 'auto 100%', label: '100% altura' },
  { value: 'auto 150%', label: '150% altura' },
  { value: 'auto 200%', label: '200% altura' },
];

export default function HeroEditor({ settings, onSave }: HeroEditorProps) {
  const [form, setForm] = useState<HeroSettings>({ ...settings });
  const [saved, setSaved] = useState(false);

  const update = (field: keyof HeroSettings, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    setForm({ ...settings });
    setSaved(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
            Editar Hero
          </h2>
          <p className="text-foreground-500 text-sm mt-1">
            Personalize o banner principal da página inicial.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm text-foreground-600 border border-background-300 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Descartar
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              saved
                ? 'bg-green-500 text-white'
                : 'bg-primary-500 hover:bg-primary-600 text-white'
            }`}
          >
            {saved ? (
              <><i className="ri-checkbox-circle-line"></i> Salvo!</>
            ) : (
              <><i className="ri-save-line"></i> Salvar alterações</>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left — Text Fields */}
        <div className="space-y-5">
          <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-5">
            <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
              <i className="ri-text text-primary-500"></i>
              Textos
            </h3>

            {/* Tagline */}
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                Tagline <span className="font-normal text-foreground-400 normal-case tracking-normal">(linha pequena acima do título)</span>
              </label>
              <input
                type="text"
                value={form.tagline}
                onChange={e => update('tagline', e.target.value)}
                placeholder="Ex: Para uma vida feliz ao lado do seu gato"
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
              />
            </div>

            {/* Heading */}
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                Título Principal
              </label>
              <textarea
                value={form.heading}
                onChange={e => update('heading', e.target.value)}
                placeholder="Ex: Tudo sobre o universo felino."
                rows={3}
                maxLength={120}
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none font-heading font-bold"
              />
              <p className="text-xs text-foreground-400 text-right mt-1">{form.heading.length}/120</p>
            </div>

            {/* Subheading */}
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                Subtítulo / Descrição
              </label>
              <textarea
                value={form.subheading}
                onChange={e => update('subheading', e.target.value)}
                placeholder="Ex: Comportamento, saúde e bem-estar..."
                rows={3}
                maxLength={300}
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none"
              />
              <p className="text-xs text-foreground-400 text-right mt-1">{form.subheading.length}/300</p>
            </div>
          </div>

          {/* Button */}
          <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
            <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
              <i className="ri-cursor-line text-primary-500"></i>
              Botão CTA
            </h3>
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                Texto do Botão
              </label>
              <input
                type="text"
                value={form.buttonText}
                onChange={e => update('buttonText', e.target.value)}
                placeholder="Ex: Explorar artigos"
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                Link do Botão
              </label>
              <input
                type="text"
                value={form.buttonLink}
                onChange={e => update('buttonLink', e.target.value)}
                placeholder="Ex: /blog ou /categoria/saude"
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
              />
            </div>

            {/* Preview Button */}
            <div>
              <p className="text-xs text-foreground-400 mb-2">Pré-visualização:</p>
              <span className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm font-bold px-5 py-2.5 rounded-full whitespace-nowrap">
                {form.buttonText || 'Explorar artigos'} <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </div>
        </div>

        {/* Right — Image */}
        <div className="space-y-5">
          <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
            <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
              <i className="ri-image-line text-primary-500"></i>
              Imagem do Hero
            </h3>

            {/* Current Preview */}
            {form.image && (
              <div className="w-full h-48 rounded-lg overflow-hidden border border-background-200">
                <img
                  src={form.image}
                  alt="Preview hero"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}

            {/* URL Input */}
            <div>
              <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                URL da Imagem
              </label>
              <input
                type="text"
                value={form.image}
                onChange={e => update('image', e.target.value)}
                placeholder="https://..."
                className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
              />
            </div>
          </div>

          {/* Position & Zoom Controls */}
          <div className="bg-background-50 border border-background-200 rounded-xl p-5 space-y-4">
            <h3 className="font-bold text-foreground-950 text-sm flex items-center gap-2">
              <i className="ri-settings-4-line text-primary-500"></i>
              Posição e Zoom
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                  Posição da Imagem
                </label>
                <select
                  value={form.bgPosition}
                  onChange={e => update('bgPosition', e.target.value)}
                  className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
                >
                  {bgPositions.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                  Ajuste / Zoom
                </label>
                <select
                  value={form.bgSize}
                  onChange={e => update('bgSize', e.target.value)}
                  className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
                >
                  {bgSizes.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Live Preview card */}
          <div className="bg-background-100 border border-background-200 rounded-xl p-5">
            <h3 className="font-bold text-foreground-950 text-sm mb-3 flex items-center gap-2">
              <i className="ri-eye-line text-primary-500"></i>
              Pré-visualização do Texto
            </h3>
            <div className="space-y-2">
              <p className="text-primary-500 text-xs font-bold uppercase tracking-widest">
                {form.tagline || '—'}
              </p>
              <p className="font-heading font-black text-foreground-950 text-xl leading-tight">
                {form.heading || '—'}
              </p>
              <p className="text-foreground-600 text-xs leading-relaxed">
                {form.subheading || '—'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}